export type AssistantPageContext = {
  page: string;
  source?: string;
  entities?: AssistantPageContextEntity[];
  metadata?: Record<string, string | number | boolean | null | undefined>;
};

type InternationalizedTextValue =
  | string
  | {
      'pt-BR'?: string;
      'en-US'?: string;
    };

type AssistantRouteDefinition = {
  page?: {
    title?: InternationalizedTextValue;
  };
  routes?: AssistantRouteDefinition[];
  sidebar?: {
    title?: InternationalizedTextValue;
    [key: string]: unknown;
  };
  tabs?: AssistantRouteDefinition[];
  url?: string;
  [key: string]: unknown;
};

type AssistantPageContextEntity = {
  id?: string;
  kind: 'vehicle' | 'driver' | 'device';
  label?: string;
};

export type VehicleMapAssistantPromptContext = {
  vehicleId?: string;
  vehicleLabel?: string;
  licensePlate?: string;
  driverId?: string;
  driverName?: string | null;
  deviceId?: string;
  cobliId?: string;
  windowHours?: number;
};

const PAGE_CONTEXT_PROMPT_PREFIX = '[[AI_PAGE_CONTEXT::';
const PAGE_CONTEXT_PROMPT_SUFFIX = '::AI_PAGE_CONTEXT]]';
const PAGE_CONTEXT_PROMPT_REGEX = /\n*\[\[AI_PAGE_CONTEXT::[\s\S]*?::AI_PAGE_CONTEXT\]\]\s*$/;

const clean = (value?: string | null) => value?.trim() || undefined;

export const formatPageContextForPrompt = (context: AssistantPageContext) => `${PAGE_CONTEXT_PROMPT_PREFIX}${JSON.stringify(context)}${PAGE_CONTEXT_PROMPT_SUFFIX}`;

export const stripPageContextPromptMetadata = (value: string) => value.replace(PAGE_CONTEXT_PROMPT_REGEX, '').trimEnd();

const normalizePathname = (value: string) => {
  const [pathWithoutQuery] = value.split(/[?#]/);
  const normalizedPath = pathWithoutQuery || '/';

  if (normalizedPath === '/') return normalizedPath;

  return normalizedPath.replace(/\/+$/, '');
};

const getPathSegments = (value: string) => normalizePathname(value).split('/').filter(Boolean);

const routeMatchesPath = (routeUrl: string, pathname: string) => {
  const routePath = normalizePathname(routeUrl);
  const currentPath = normalizePathname(pathname);

  if (routePath === currentPath) return true;

  const routeSegments = getPathSegments(routePath);
  const pathSegments = getPathSegments(currentPath);

  if (routeSegments.length !== pathSegments.length) return false;

  return routeSegments.every((routeSegment, index) => routeSegment.startsWith(':') || routeSegment === pathSegments[index]);
};

const routeStartsPath = (routeUrl: string, pathname: string) => {
  const routePath = normalizePathname(routeUrl);
  const currentPath = normalizePathname(pathname);

  return routePath !== '/' && currentPath.startsWith(`${routePath}/`);
};

const flattenAssistantRoutes = (routes: AssistantRouteDefinition[]): AssistantRouteDefinition[] =>
  routes.flatMap((route) => [route, ...flattenAssistantRoutes(route.routes ?? []), ...flattenAssistantRoutes(route.tabs ?? [])]);

const resolveInternationalizedText = (value: InternationalizedTextValue | undefined, language: string) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;

  return value[language as keyof typeof value] ?? value['pt-BR'] ?? value['en-US'] ?? Object.values(value).find(Boolean);
};

export const resolveAssistantPageContextLabel = (routes: AssistantRouteDefinition[], pathname: string, language = 'pt-BR') => {
  const routesWithUrls = flattenAssistantRoutes(routes).filter((route): route is AssistantRouteDefinition & { url: string } => Boolean(route.url));
  const exactMatches = routesWithUrls.filter((route) => routeMatchesPath(route.url, pathname));
  const prefixMatches = exactMatches.length ? [] : routesWithUrls.filter((route) => routeStartsPath(route.url, pathname));
  const matches = exactMatches.length ? exactMatches : prefixMatches;
  const bestMatch = matches.reduce<(typeof matches)[number] | undefined>((currentBestMatch, route) => {
    if (!currentBestMatch) return route;

    return getPathSegments(route.url).length > getPathSegments(currentBestMatch.url).length ? route : currentBestMatch;
  }, undefined);

  return resolveInternationalizedText(bestMatch?.sidebar?.title ?? bestMatch?.page?.title, language);
};

export type PromptSuggestionAssistantPromptContext = {
  page: string;
  pageLabel?: string;
  prompt: string;
  source?: string;
  tool: string;
};

export const buildPromptSuggestionAssistantPrompt = ({ page, pageLabel, prompt, source = 'prompt-suggestion', tool }: PromptSuggestionAssistantPromptContext) => {
  const pageContext = formatPageContextForPrompt({
    page,
    source,
    metadata: {
      pageLabel,
      tool,
    },
  });

  return `${prompt.trimEnd()}\n${pageContext}`;
};

export const buildVehicleMapAssistantPrompt = ({
  vehicleId,
  vehicleLabel,
  licensePlate,
  driverId,
  driverName,
  deviceId,
  cobliId,
  windowHours = 24,
}: VehicleMapAssistantPromptContext) => {
  const vehicleName = clean(vehicleLabel) ?? clean(licensePlate) ?? clean(cobliId) ?? clean(vehicleId) ?? 'veiculo selecionado';
  const normalizedLicensePlate = clean(licensePlate);
  const normalizedDriverName = clean(driverName);
  const entities: AssistantPageContextEntity[] = [
    { kind: 'vehicle', id: clean(vehicleId), label: vehicleName },
    { kind: 'driver', id: clean(driverId), label: normalizedDriverName },
    { kind: 'device', id: clean(deviceId), label: clean(cobliId) },
  ];

  const visiblePrompt = [
    `Analise o veiculo ${vehicleName} no mapa considerando as ultimas ${windowHours} horas.`,
    normalizedLicensePlate ? `Placa: ${normalizedLicensePlate}.` : undefined,
    normalizedDriverName ? `Motorista: ${normalizedDriverName}.` : 'Motorista: nao informado.',
    'Olhe seguranca, produtividade, combustivel e manutencao.',
    'Responda em bullets curtos priorizados por urgencia ou em uma linha se tudo estiver normal.',
  ]
    .filter(Boolean)
    .join('\n');

  const pageContext = formatPageContextForPrompt({
    page: '/monitoring',
    source: 'vehicle-map-popup',
    entities: entities.filter((entity) => entity.id || entity.label),
    metadata: {
      licensePlate: normalizedLicensePlate,
      windowHours,
    },
  });

  return `${visiblePrompt}\n${pageContext}`;
};

export type RiskRecognitionInvestigatePromptContext = {
  driverName?: string;
  mainFactorTitle?: string;
  driverId?: string;
  vehicleId?: string;
  licensePlate?: string;
  deviceId?: string;
  ruleName?: string;
};

const buildGenericInvestigatePrompt = (driverName: string, mainFactor: string) =>
  `O motorista ${driverName} apresentou ${mainFactor} hoje. ` +
  'O comportamento de direção é padrão para este motorista? ' +
  `Analise o histórico dos últimos 7 dias trabalhados do condutor ${driverName}, ` +
  'crie um perfil de condução dele e me dê ações imediatas para resolver a situação.';

const buildLongJourneyInvestigatePrompt = (driverRef: string) =>
  `Motorista ${driverRef} disparou jornada prolongada.

1. productivityEntityExplanationTool (driver, 30d).
2. Leia APENAS: drivenTimeInMinutes, activeDaysCount.
3. executeCodeTool: drivenTimeInMinutes / activeDaysCount / 60

Responda em 3 partes curtas:

1. Histórico: ele dirige em média X h/dia ativo.
2. Hoje ultrapassou 5h30 — pausa de 30 min obrigatória (Lei 13.103/2015).
3. Recomendação ao gestor: 1 ação imediata.
   Foque em UM destes ângulos:
   - contatar o motorista para parar agora
   - identificar parada segura próxima na rota
   - bloquear atribuição de nova rota até cumprir descanso de 11h
   - registrar a pausa para compliance

   NÃO recomende: revisar paradas, redistribuir jornada estrutural, ou qualquer mudança operacional que não seja resposta direta ao alerta de fadiga.`;

export const buildRiskRecognitionInvestigatePrompt = ({
  driverName,
  mainFactorTitle,
  driverId,
  vehicleId,
  licensePlate,
  deviceId,
  ruleName,
}: RiskRecognitionInvestigatePromptContext) => {
  const normalizedDriverName = clean(driverName) ?? 'este motorista';
  const normalizedFactor = clean(mainFactorTitle) ?? 'um comportamento de risco';

  const visiblePrompt =
    clean(ruleName) === 'long_journey'
      ? buildLongJourneyInvestigatePrompt(clean(driverId) ?? normalizedDriverName)
      : buildGenericInvestigatePrompt(normalizedDriverName, normalizedFactor);

  const entities: AssistantPageContextEntity[] = [
    { kind: 'driver', id: clean(driverId), label: clean(driverName) },
    { kind: 'vehicle', id: clean(vehicleId), label: clean(licensePlate) },
    { kind: 'device', id: clean(deviceId) },
  ];

  const pageContext = formatPageContextForPrompt({
    page: '/monitoring',
    source: 'risk-recognition-drawer',
    entities: entities.filter((entity) => entity.id || entity.label),
    metadata: {
      ruleName: clean(ruleName),
      mainFactor: clean(mainFactorTitle),
    },
  });

  return `${visiblePrompt}\n${pageContext}`;
};
